import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { WebsiteBuilderToolbarMenuSections } from "../../../WebsiteBuilderToolbarMenuSections";
import { IWebsiteBuilderGlobalStyles } from "redux/reducers/models/websiteBuilder";
import { cloneDeep } from "lodash";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { websiteBuilderUpdateGlobalStyles } from "redux/actions/websiteBuilder/websiteBuilder";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { Select } from "components/select/index";
import Input from "components/form/input/Input";
import { isWholeNumber } from "helpers/Helpers";

const fonts = [
  { label: "Arial", content: "Arial, sans-serif" },
  { label: "Georgia", content: "Georgia, serif" },
  { label: "Verdana", content: "Verdana, sans-serif" },
  { label: "Trebuchet", content: "Trebuchet MS, sans-serif" },
  { label: "Courier New", content: "Courier New, monospace" },
];

const DEFAULT_FONT_PLACEHOLDER = "";

export function FontsView() {
  const { Option } = Select;
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const globalStyles = useAppSelector(store => store.websiteBuilderStore.globalStyles);
  const [updatedGlobalStyles, setUpdatedGlobalStyles] = useState<IWebsiteBuilderGlobalStyles>(cloneDeep(globalStyles));

  const [paragraphFontSizeField, setParagraphFontSizeField] = useState<string>(
    globalStyles.paragraph.fontSize == null ? "" : String(globalStyles.paragraph.fontSize),
  );

  function isParagraphFontSizeFieldValid(value: string) {
    if (value === "") {
      return true;
    }

    const numValue = Number(value);

    return isWholeNumber(value) && numValue >= 12 && numValue <= 48;
  }

  const paragraphFontSizeFieldValid = isParagraphFontSizeFieldValid(paragraphFontSizeField);

  function dispatchGlobalStyles(
    prevGlobalStyles: IWebsiteBuilderGlobalStyles,
    nextGlobalStyles: IWebsiteBuilderGlobalStyles,
  ) {
    dispatch(
      websiteBuilderUpdateGlobalStyles({
        globalStyles: cloneDeep(nextGlobalStyles),
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: globalStyles,
    updatedState: updatedGlobalStyles,
    setUpdatedState: setUpdatedGlobalStyles,
    dispatchState: dispatchGlobalStyles,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.SHORT,
  });

  function handleChangeParagraphFontSizeField(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;

    setParagraphFontSizeField(value);

    if (isParagraphFontSizeFieldValid(value)) {
      handleDebouncedUpdateState(prev => ({
        ...prev,
        paragraph: {
          ...prev.paragraph,
          fontSize: value === "" ? null : Number(value),
        },
      }));
    }
  }

  function renderFontOptions() {
    return (
      <>
        <Option value={DEFAULT_FONT_PLACEHOLDER}>&nbsp;</Option>
        {fonts.map((font, index) => (
          <Option key={index} value={font.content}>
            {font.label}
          </Option>
        ))}
      </>
    );
  }

  function renderHeadingFontSection() {
    if (updatedGlobalStyles?.heading) {
      return (
        <Select
          label={t("Font")}
          onChange={font =>
            handleUpdateState(prev => ({
              ...prev,
              heading: {
                ...prev.heading,
                font: font === DEFAULT_FONT_PLACEHOLDER ? null : font,
              },
            }))
          }
          defaultValue={updatedGlobalStyles.heading.font ?? DEFAULT_FONT_PLACEHOLDER}
        >
          {renderFontOptions()}
        </Select>
      );
    }
  }

  function renderParagraphFontSection() {
    if (updatedGlobalStyles?.paragraph) {
      return (
        <>
          <Select
            label={t("Font")}
            onChange={font =>
              handleUpdateState(prev => ({
                ...prev,
                paragraph: {
                  ...prev.paragraph,
                  font: font === DEFAULT_FONT_PLACEHOLDER ? null : font,
                },
              }))
            }
            defaultValue={updatedGlobalStyles.paragraph.font ?? DEFAULT_FONT_PLACEHOLDER}
          >
            {renderFontOptions()}
          </Select>
          <Input
            label={t("Size (px)")}
            onChange={handleChangeParagraphFontSizeField}
            value={paragraphFontSizeField}
            error={!paragraphFontSizeFieldValid}
          />
        </>
      );
    }
  }

  return (
    <WebsiteBuilderToolbarMenuSections
      sections={[
        {
          title: t("Headings"),
          content: renderHeadingFontSection(),
        },
        {
          title: t("Paragraphs"),
          content: renderParagraphFontSection(),
        },
      ]}
    />
  );
}
